import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Fab, Grow, Paper, Tooltip, Typography, Zoom } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import aiChatService from '../../services/aiChatService';
import AiChatWindow from './AiChatWindow';

/**
 * Sağ altta yüzen AI chat butonu (ImpersonationFab deseni).
 * Açılışta /api/AiChat/status sorgulanır: sunucuda API anahtarı yoksa hiç render edilmez.
 * Anahtar varsa buton TÜM şirketlerde görünür — paketi olmayan şirketlerde chat açılır
 * ama gönderim paket tanıtımına yönlendirir (AiChatWindow packageActive=false).
 *
 * Karşılama modu: sayfa ilk yüklendiğinde buton büyütülmüş gösterilir ve solunda
 * "Merhaba, nasıl yardımcı olabilirim?" balonu çıkar; kullanıcı herhangi bir yere
 * tıklayınca normal boyuta döner (butona tıklarsa chat de açılır).
 */
// Yatay sürükleme sınırları: buton 56px genişliğinde, iki kenardan 16px pay bırakılır.
const FAB_SIZE = 56;
const FAB_MARGIN = 16;
const FAB_RIGHT_STORAGE_KEY = 'aiFabRight';

const clampFabRight = (value: number): number => {
  const max = Math.max(FAB_MARGIN, window.innerWidth - FAB_SIZE - FAB_MARGIN);
  return Math.min(max, Math.max(FAB_MARGIN, value));
};

const readStoredFabRight = (): number => {
  try {
    const raw = localStorage.getItem(FAB_RIGHT_STORAGE_KEY);
    const parsed = raw === null ? NaN : Number(raw);
    return Number.isFinite(parsed) ? clampFabRight(parsed) : FAB_MARGIN;
  } catch {
    return FAB_MARGIN;
  }
};

const AiChatFab: React.FC = () => {
  const { t } = useTranslation('common');
  const { currentUser, userDetails, isImpersonating } = useAuth();

  const [visible, setVisible] = useState(false);
  const [packageActive, setPackageActive] = useState(false);
  const [agentEnabled, setAgentEnabled] = useState(false);
  const [planEnabled, setPlanEnabled] = useState(false);
  const [remainingTokens, setRemainingTokens] = useState(0);
  const [open, setOpen] = useState(false);
  const [greeting, setGreeting] = useState(false);

  // Butonun sağ kenardan uzaklığı (px) — kullanıcı sağa/sola sürükleyebilir, tercih kalıcıdır.
  const [fabRight, setFabRight] = useState<number>(readStoredFabRight);
  const dragRef = useRef<{ pointerId: number; startX: number; startRight: number; moved: boolean } | null>(null);
  // Sürükleme sonrası pointerup'ı takip eden click'in chat'i aç/kapamasını engeller.
  const suppressClickRef = useRef(false);

  const companyId = userDetails?.companyId;

  // ÖNEMLİ: setPointerCapture KULLANMIYORUZ — capture, click olayını FAB yerine
  // dış kutuya yönlendirdiği için butona tıklanınca chat açılmıyordu. Bunun yerine
  // pointerdown'da window'a geçici move/up dinleyicileri takılır; hızlı sürüklemede
  // işaretçi kutunun dışına çıksa bile takip devam eder, click ise FAB'da normal işler.
  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current) return; // ikinci parmak/tuş gelirse yok say
    const drag = { pointerId: e.pointerId, startX: e.clientX, startRight: fabRight, moved: false };
    dragRef.current = drag;

    const onMove = (ev: PointerEvent) => {
      if (ev.pointerId !== drag.pointerId) return;
      const deltaX = drag.startX - ev.clientX; // sola sürükleyince right artar
      if (!drag.moved && Math.abs(deltaX) < 5) return; // titrek tıklamaları sürükleme sayma
      drag.moved = true;
      setFabRight(clampFabRight(drag.startRight + deltaX));
    };

    const onEnd = (ev: PointerEvent) => {
      if (ev.pointerId !== drag.pointerId) return;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onEnd);
      window.removeEventListener('pointercancel', onEnd);
      dragRef.current = null;
      if (drag.moved) {
        // Sürüklemeyi takip eden click chat'i aç/kapamasın; bayrak yalnızca bu
        // olay döngüsü için geçerli — click hiç gelmezse bir sonraki tıklamayı yutmasın.
        suppressClickRef.current = true;
        setTimeout(() => {
          suppressClickRef.current = false;
        }, 0);
        try {
          localStorage.setItem(FAB_RIGHT_STORAGE_KEY, String(clampFabRight(drag.startRight + (drag.startX - ev.clientX))));
        } catch {
          // localStorage erişilemezse konum yalnızca bu oturumda kalır
        }
      }
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onEnd);
  }, [fabRight]);

  // Pencere küçülürse buton ekran dışında kalmasın
  useEffect(() => {
    const onResize = () => setFabRight((prev) => clampFabRight(prev));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    let active = true;
    setVisible(false);
    setOpen(false);

    if (!companyId || !currentUser?.uid) return undefined;

    aiChatService
      .getAiChatStatus()
      .then((status) => {
        if (!active) return;
        setVisible(status.configured);
        setPackageActive(status.enabled);
        setAgentEnabled(status.agentEnabled);
        setPlanEnabled(status.planEnabled);
        setRemainingTokens(status.remainingTokens);
        // Karşılama yalnızca sayfa ilk yüklendiğinde (bu mount'ta) gösterilir
        if (status.configured) setGreeting(true);
      })
      .catch(() => {
        // Sessiz degrade: durum alınamazsa buton gösterilmez
        if (active) setVisible(false);
      });

    return () => {
      active = false;
    };
  }, [companyId, currentUser?.uid]);

  // Karşılama modunda ilk tıklamada (sayfanın neresi olursa olsun) normale dön.
  // pointerdown, FAB'ın kendi onClick'inden önce çalışır — butona tıklanırsa chat yine açılır.
  useEffect(() => {
    if (!greeting) return undefined;
    const dismiss = () => setGreeting(false);
    document.addEventListener('pointerdown', dismiss, true);
    return () => document.removeEventListener('pointerdown', dismiss, true);
  }, [greeting]);

  if (!visible) return null;

  const fabBottom = isImpersonating ? 80 : 16;

  return (
    <>
      {open && (
        <AiChatWindow
          onClose={() => setOpen(false)}
          remainingTokens={remainingTokens}
          onRemainingTokensChange={setRemainingTokens}
          packageActive={packageActive}
          agentEnabled={agentEnabled}
          planEnabled={planEnabled}
        />
      )}

      {/* Karşılama balonu (butonun solunda) */}
      <Grow in={greeting} style={{ transformOrigin: 'bottom right' }}>
        <Paper
          elevation={6}
          onClick={() => {
            setGreeting(false);
            setOpen(true);
          }}
          sx={{
            position: 'fixed',
            // FAB karşılamada 2x büyüdüğü için (56px → 112px) balon daha solda durur;
            // buton sürüklenmişse balon da onunla birlikte kayar
            right: fabRight + 124,
            bottom: fabBottom + 24,
            zIndex: 2050,
            px: 2.5,
            py: 1.75,
            borderRadius: '20px 20px 6px 20px',
            maxWidth: 320,
            cursor: 'pointer',
            bgcolor: 'background.paper',
          }}
        >
          <Typography sx={{ fontSize: 19, fontWeight: 600, lineHeight: 1.35 }}>
            {t('aiChat.greeting')}
          </Typography>
        </Paper>
      </Grow>

      {/* Zoom kendi transform'unu sardığı öğeye yazdığı için scale(2) FAB'a değil,
          dış kutuya Zoom, FAB'a büyütme uygulanır (aksi halde büyüme ezilir). */}
      <Zoom in>
        <Box
          onPointerDown={handlePointerDown}
          sx={{
            position: 'fixed',
            right: fabRight,
            // Impersonation FAB'i de sağ altta durur — çakışmayı önle
            bottom: fabBottom,
            zIndex: 2050,
            // Dokunmatik cihazlarda sürükleme sayfayı kaydırmasın
            touchAction: 'none',
            cursor: 'grab',
            '&:active': { cursor: 'grabbing' },
          }}
        >
          <Tooltip title={t('aiChat.fabTooltip')}>
            <Fab
              color="primary"
              onClick={() => {
                // Sürükleme bittiğinde gelen click chat'i açmasın
                if (suppressClickRef.current) {
                  suppressClickRef.current = false;
                  return;
                }
                setOpen((prev) => !prev);
              }}
              aria-label={t('aiChat.fabTooltip')}
              sx={{
                transformOrigin: 'bottom right',
                transition: 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
                transform: greeting ? 'scale(2)' : 'scale(1)',
                // Karşılama modunda dikkat çeken nabız halkası
                '&::after': greeting
                  ? {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '50%',
                      border: '3px solid',
                      borderColor: 'primary.main',
                      animation: 'aiFabPulse 1.6s ease-out infinite',
                    }
                  : undefined,
                '@keyframes aiFabPulse': {
                  '0%': { transform: 'scale(1)', opacity: 0.7 },
                  '100%': { transform: 'scale(1.55)', opacity: 0 },
                },
              }}
            >
              {open ? <CloseIcon /> : <AutoAwesomeIcon />}
            </Fab>
          </Tooltip>
        </Box>
      </Zoom>
    </>
  );
};

export default AiChatFab;
